import type { PhotoRow, SuggestionRow } from '@/schema/estate';
import type { CaptureContext } from '@/services/captureContext';
import { aliasesOf, type itemsByEstate } from '@/services/itemService';
import { readMedia } from '@/services/photoService';
import type { TagFilter } from '@/services/tagFilters';

// useSelect caches its snapshot and compares with `isEqual` (default Object.is). Rowboat
// rows are rebuilt on every read, so selectors here return new objects per call; a
// shape-aware `isEqual` over the rendered fields keeps the snapshot stable between data
// changes (Object.is would re-render forever).

export const sameRows = (
  a: Array<{ id: string; name: string }>,
  b: Array<{ id: string; name: string }>,
) => a.length === b.length && a.every((row, i) => row.id === b[i].id && row.name === b[i].name);

export const sameContext = (a: CaptureContext, b: CaptureContext) =>
  a.estateId === b.estateId && a.itemGroupId === b.itemGroupId && a.categoryId === b.categoryId;

type ItemRow = ReturnType<typeof itemsByEstate>[number];

export const sameItems = (a: ItemRow[], b: ItemRow[]) =>
  a.length === b.length &&
  a.every(
    (row, i) =>
      row.id === b[i].id &&
      row.inventory_number === b[i].inventory_number &&
      row.name === b[i].name &&
      row.description === b[i].description &&
      row.item_group_id === b[i].item_group_id &&
      aliasesOf(row).join('|') === aliasesOf(b[i]).join('|'),
  );

export const samePhotos = (a: PhotoRow[], b: PhotoRow[]) =>
  a.length === b.length &&
  a.every(
    (p, i) =>
      p.id === b[i].id &&
      readMedia(p.storage_key)?.hash === readMedia(b[i].storage_key)?.hash &&
      readMedia(p.thumbnail_key)?.hash === readMedia(b[i].thumbnail_key)?.hash,
  );

export const sameTagFilters = (a: TagFilter[], b: TagFilter[]) =>
  a.length === b.length &&
  a.every(
    (f, i) =>
      f.name === b[i].name &&
      f.itemIds.size === b[i].itemIds.size &&
      [...f.itemIds].every((id) => b[i].itemIds.has(id)),
  );

export function sameSuggestions(a: SuggestionRow[], b: SuggestionRow[]): boolean {
  return (
    a.length === b.length &&
    a.every((s, i) => s.id === b[i]?.id && s.state === b[i]?.state && s.value === b[i]?.value)
  );
}
